import { useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { serverErrorActive } from '../config/axiosInterceptors';
import { BACKEND_URL } from '../config/api';

const API = BACKEND_URL;

/**
 * Heartbeat Component
 * Sends periodic heartbeat to keep user's online status fresh.
 * Detects session loss with DOUBLE verification before triggering logout.
 */
const Heartbeat = () => {
  const { user, logout } = useContext(AuthContext);
  const isVerifyingRef = useRef(false);

  // Double-verify: two checks 2s apart, both must return 401 to confirm session death
  const doubleVerifyAndLogout = useCallback(async () => {
    if (isVerifyingRef.current) return;
    isVerifyingRef.current = true;
    try {
      const res1 = await fetch(`${API}/api/auth/me`, {
        credentials: 'include',
        signal: AbortSignal.timeout(8000),
      });
      if (res1.status !== 401) return; // Session is valid

      // First check returned 401 — wait 2s and confirm
      await new Promise(r => setTimeout(r, 2000));

      const res2 = await fetch(`${API}/api/auth/me`, {
        credentials: 'include',
        signal: AbortSignal.timeout(8000),
      });
      if (res2.status === 401) {
        console.warn('[Heartbeat] Session confirmed expired (double verified)');
        logout();
      }
      // Second check passed = transient issue, don't logout
    } catch {
      // Network error — transient, don't logout
    } finally {
      isVerifyingRef.current = false;
    }
  }, [logout]);

  const sendHeartbeat = useCallback(async () => {
    if (!user || isVerifyingRef.current || serverErrorActive) return;

    try {
      const res = await axios.post(`${API}/api/heartbeat`, {}, { withCredentials: true, timeout: 5000 });

      if (res.data?.status === 'unauthenticated') {
        await doubleVerifyAndLogout();
      }
    } catch (error) {
      // Only verify on explicit 401 — ignore 502, 503, 520, network, timeout
      if (error?.response?.status === 401) {
        await doubleVerifyAndLogout();
      }
    }
  }, [user, doubleVerifyAndLogout]);

  useEffect(() => {
    if (!user) return;

    // Send initial heartbeat
    sendHeartbeat();

    // Send heartbeat every 3 minutes
    const interval = setInterval(sendHeartbeat, 180000);

    return () => clearInterval(interval);
  }, [user, sendHeartbeat]);

  return null;
};

export default Heartbeat;
